// CommandsListTable – table of commands for a request with sort and selection.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import { CommandRow, SortKey } from '../../../types/Commands';
import { getAckPercent, isInProgressStatus } from './CommandsListHelpers';
import { formatTime, toMillis, normStatus, formatElapsed } from '../../helpers/helpers';

// ---- Types ----

type CommandsTableProps = {
    rows: { id: string; row: CommandRow }[];
    requestSort: (key: SortKey) => void;
    getSortIndicator: (key: SortKey) => string;

    selectedDeviceIds: string[];
    allSelected: boolean;
    onToggleSelectAll: () => void;
    onToggleSelectDevice: (deviceId?: string) => void;
};

// ---- Component ----

export function CommandsTable(props: CommandsTableProps): JSX.Element {
    const {
        rows,
        requestSort,
        getSortIndicator,
        selectedDeviceIds,
        allSelected,
        onToggleSelectAll,
        onToggleSelectDevice,
    } = props;

    const selectedSet = new Set(selectedDeviceIds);

    const hasAnyInProgress = useMemo(() => {
        for (const it of rows) {
            const st = it?.row?.ddb?.status;
            if (isInProgressStatus(st)) return true;
        }
        return false;
    }, [rows]);

    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    // Tick every second when any command is in progress (for elapsed timer).
    useEffect(() => {
        if (!hasAnyInProgress) return;
        setNowMs(Date.now());
        const t = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(t);
    }, [hasAnyInProgress]);

    return (
        <div className="overflow-x-auto rounded-lg shadow border max-h-[70vh] overflow-y-auto text-sm">
            <table className="min-w-full table-auto text-left">
                <thead className="bg-gray-100 dark:bg-slate-800 text-gray-700">
                    <tr>
                        <th className="px-3 py-2 text-xs font-medium select-none w-[44px]">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => onToggleSelectAll()}
                            />
                        </th>

                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('commandId')}
                        >
                            commandId {getSortIndicator('commandId')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('deviceId')}
                        >
                            deviceId {getSortIndicator('deviceId')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('type')}
                        >
                            type {getSortIndicator('type')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('targetVersion')}
                        >
                            targetVersion {getSortIndicator('targetVersion')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('requestStatusCode')}
                        >
                            requestStatusCode {getSortIndicator('requestStatusCode')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('status')}
                        >
                            status {getSortIndicator('status')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('ackState')}
                        >
                            ack.state {getSortIndicator('ackState')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('ackReason')}
                        >
                            ack.reason {getSortIndicator('ackReason')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('ackAccepted')}
                        >
                            ack.accepted {getSortIndicator('ackAccepted')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('ackPercent')}
                        >
                            ack.percent {getSortIndicator('ackPercent')}
                        </th>
                        <th
                            className="px-3 py-2 text-xs font-medium cursor-pointer select-none"
                            onClick={() => requestSort('updatedAt')}
                        >
                            updatedAt {getSortIndicator('updatedAt')}
                        </th>
                    </tr>
                </thead>
                <tbody className="text-gray-800 dark:text-slate-200">
                    {rows.map(({ id, row }, idx) => {
                        const { ddb, ack } = row;
                        const updated = formatTime(ddb.updatedAt);

                        const deviceId = typeof ddb.deviceId === 'string' ? ddb.deviceId : '';
                        const checked = !!deviceId && selectedSet.has(deviceId);

                        const targetVersion =
                            typeof ddb.targetVersion === 'string' && ddb.targetVersion.trim().length > 0
                                ? ddb.targetVersion
                                : '';

                        const statusLabel = ddb.status || '—';
                        const statusNorm = normStatus(statusLabel);
                        const statusLower = statusLabel.toLowerCase();

                        let statusClass = '';
                        if (statusLower === 'succeeded' || statusLower === 'success') {
                            statusClass = 'text-green-700';
                        } else if (statusLower === 'failed' || statusLower === 'error') {
                            statusClass = 'text-red-700';
                        }

                        const updatedAtMs =
                            typeof ddb.updatedAt === 'number' && Number.isFinite(ddb.updatedAt) && ddb.updatedAt > 0
                                ? toMillis(ddb.updatedAt)
                                : null;

                        const showTimer =
                            isInProgressStatus(statusNorm) &&
                            typeof updatedAtMs === 'number' &&
                            typeof nowMs === 'number' &&
                            nowMs > 0;

                        const elapsedMs = showTimer ? nowMs - (updatedAtMs as number) : 0;

                        let ackAcceptedLabel = '—';
                        let ackAcceptedClass = '';
                        if (typeof ack?.accepted === 'boolean') {
                            ackAcceptedLabel = String(ack.accepted);
                            ackAcceptedClass = ack.accepted ? 'text-green-700' : 'text-red-700';
                        }

                        const percentRaw = getAckPercent(ack);
                        const hasPercent = percentRaw >= 0 && !Number.isNaN(percentRaw);
                        const clampedPercent = hasPercent ? Math.max(0, Math.min(100, percentRaw)) : 0;
                        const percentLabel = hasPercent ? `${clampedPercent}%` : '—';

                        return (
                            <tr
                                key={id}
                                className={idx % 2 === 0 ? 'bg-white dark:bg-slate-900 border-t' : 'bg-gray-50 border-t'}
                            >
                                <td className="px-3 py-1.5 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={!deviceId}
                                        onChange={() => onToggleSelectDevice(deviceId)}
                                    />
                                </td>

                                <td className="px-3 py-1.5 text-xs break-all">{ddb.commandId || '—'}</td>
                                <td className="px-3 py-1.5 text-xs">{deviceId || '—'}</td>
                                <td className="px-3 py-1.5 text-xs">{ddb.type || '—'}</td>
                                <td className="px-3 py-1.5 text-xs">{targetVersion || '—'}</td>
                                <td className="px-3 py-1.5 text-xs">
                                    {typeof ddb.requestStatusCode === 'number' ? ddb.requestStatusCode : '—'}
                                </td>

                                <td className={`px-3 py-1.5 text-xs ${statusClass}`}>
                                    <span className="inline-flex items-center">
                                        <span>{statusLabel}</span>
                                        {showTimer && (
                                            <span className="ml-2 text-[11px] text-gray-500 dark:text-slate-400 tabular-nums">
                                                {formatElapsed(elapsedMs)}
                                            </span>
                                        )}
                                    </span>
                                </td>

                                <td className="px-3 py-1.5 text-xs">{ack?.state || '—'}</td>
                                <td className="px-3 py-1.5 text-xs">{ack?.reason || '—'}</td>
                                <td className={`px-3 py-1.5 text-xs ${ackAcceptedClass}`}>{ackAcceptedLabel}</td>
                                <td className="px-3 py-1.5 text-xs">
                                    {hasPercent ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 h-2 bg-gray-200 rounded">
                                                <div
                                                    className="h-2 rounded bg-blue-500"
                                                    style={{ width: `${clampedPercent}%` }}
                                                />
                                            </div>
                                            <span className="text-[10px] text-gray-700 dark:text-slate-300">{percentLabel}</span>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-500 dark:text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 text-xs whitespace-nowrap">{updated || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
